import { Component, Host, h, Element, State, Prop } from '@stencil/core';
import tippy, { Placement } from 'tippy.js';
import { handlePromoView } from '@aha-app/aha-shared';

export interface AhaPromoPopover extends HTMLAhaPromoPopoverElement {}

/**
 * `aha-promo-popover` should be used with `aha-promo-popover-content`.
 * This component manages how/where we show the popover.
 */
@Component({
  tag: 'aha-promo-popover',
  styleUrl: 'aha-promo-popover.css',
  shadow: false,
})
export class AhaPromoPopover {
  @Element() host!: HTMLAhaPromoPopoverElement;

  /**
   * The placement of the popover. See Tippy.js documentation for all options: https://atomiks.github.io/tippyjs/v6/all-props/#placement
   */
  @Prop() placement?: Placement = 'auto';

  /**
   * If true, set Tippy appendTo to document.body. Otherwise, uses default of 'parent'.
   */
  @Prop() appendToBody?: boolean;

  /**
   * The snake case name of the upgrade location, found in SuiteHelper::ANALYTICS_TRACKING_PROPS.
   * The upgrade location is used to infer the promoted product, which then informs the feature ID and CTA link path.
   */
  @Prop() upgradeLocation!: string;

  @State() viewEventFired: boolean = false;

  private observer: any;
  componentDidLoad() {
    this.applyTooltip();

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!this.viewEventFired && this.upgradeLocation && entry.isIntersecting) {
            this.viewEventFired = true;
            handlePromoView(this.upgradeLocation);
          }
        });
      },
      { threshold: 0.5 },
    );

    this.observer.observe(this.host);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  private applyTooltip() {
    if (this.host) {
      const triggerElement = this.host;
      const popoverContent = triggerElement?.closest('aha-promo-popover')?.nextElementSibling;

      // Set upgrade location onto aha-promo-popover-content so we don't need to pass through separately
      popoverContent?.setAttribute('upgrade-location', this.upgradeLocation);

      if (triggerElement) {
        tippy(triggerElement, {
          content: popoverContent?.outerHTML,
          appendTo: this.appendToBody ? document.body : 'parent',
          allowHTML: true,
          interactive: true,
          theme: 'light',
          zIndex: 12000,
          placement: this.placement,
          arrow: true,
          onShow(instance) {
            // Force Tippy.js to update its position before showing
            setTimeout(() => {
              instance?.popperInstance?.update();
            }, 0);
          },
        });
        popoverContent?.toggleAttribute('hidden');
      }
    }
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
